if (!customElements.get('wlsn-tabs-switcher')) {
  class WLSNTabsSwitcher extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        tabContentContainer: 'wlsn-tab-content',
        tabContentButton: 'wlsn-tab-button',
        navTabsInner: 'wlsn-tabs-nav',
        active: 'active',
        hidden: 'hidden',
        slider: 'js-swiper',
      };

      this.bind = {
        onTabClick: this._onTabClick.bind(this),
      };

      this.navSwiperInstance = null;
    }

    /**
     * Returns key nodes for the tab layout, scoped under this custom element.
     *
     * @returns {{
     *   tabContentContainer: HTMLElement | null;
     *   navTabsInner: HTMLElement | null;
     * }}
     */
    get elements() {
      return {
        tabContentContainer: this.querySelector(this.classes.tabContentContainer),
        navTabsInner: this.querySelector(this.classes.navTabsInner),
        slider: this.querySelector(`.${this.classes.slider}`),
      };
    }

    /**
     * Wires click handlers then marks as hydrated.
     * Runs when the element is inserted into the document.
     *
     * @returns {void}
     */
    connectedCallback() {
      this._setupTabs();
      this._initNavSlider();
      this.setAttribute('data-wlsn-hydrated', '');
    }

    /**
     * Destroys the nav slider and drops tab button listeners.
     * Runs when the element is removed from the document.
     *
     * @returns {void}
     */
    disconnectedCallback() {
      this._destroyNavSlider();
      this._removeEventListeners();
    }

    /**
     * Attaches click listeners to tab buttons inside the nav container.
     *
     * @returns {void}
     */
    _setupTabs() {
      const { navTabsInner } = this.elements;
      if (!navTabsInner) return;

      const tabContentButtons = navTabsInner.querySelectorAll(this.classes.tabContentButton);
      if (!tabContentButtons || tabContentButtons.length === 0) return;

      tabContentButtons.forEach((button) => {
        button.addEventListener('click', this.bind.onTabClick);
      });
    }

    /**
     * Removes the same click handler references registered in `_setupTabs`.
     *
     * @returns {void}
     */
    _removeEventListeners() {
      const { navTabsInner } = this.elements;
      if (!navTabsInner) return;

      const tabContentButtons = navTabsInner.querySelectorAll(this.classes.tabContentButton);
      if (!tabContentButtons || tabContentButtons.length === 0) return;

      tabContentButtons.forEach((button) => {
        button.removeEventListener('click', this.bind.onTabClick);
      });
    }

    /**
     * Initialises Swiper on the nav element (markup is pre-built in liquid).
     * slidesPerView: 'auto', spaceBetween: 1.6rem, centeredSlides: true.
     *
     * @returns {void}
     */
    _initNavSlider() {
      if (typeof Swiper === 'undefined') return;

      const { slider } = this.elements;
      if (!slider) return;

      const spaceBetween = parseFloat(getComputedStyle(document.documentElement).fontSize) * 1.6;

      this.navSwiperInstance = new Swiper(slider, {
        slidesPerView: 'auto',
        spaceBetween,
        centerInsufficientSlides: true,
        grabCursor: true,
        threshold: 4
      });
    }

    /**
     * Destroys the nav Swiper instance and clears the reference.
     *
     * @returns {void}
     */
    _destroyNavSlider() {
      if (this.navSwiperInstance && !this.navSwiperInstance.destroyed) {
        this.navSwiperInstance.destroy(true, true);
        this.navSwiperInstance = null;
      }
    }

    /**
     * Reads `data-tab-id` from the clicked trigger and switches the visible panel to that id.
     *
     * @param {MouseEvent} event - Click on a tab button (listener target).
     * @returns {void}
     */
    _onTabClick(event) {
      const button = event.currentTarget;
      if (!(button instanceof HTMLElement)) return;

      const tabId = button.getAttribute('data-tab-id');
      if (!tabId) return;

      this._switchTab(tabId);
    }

    /**
     * Updates active button state and toggles `hidden` on panel nodes for the given tab id.
     * Resolves panels by attribute equality so tab ids are not interpolated into CSS selectors.
     *
     * @param {string} tabId - Value of `data-tab-id` on the button and matching content node.
     * @returns {void}
     */
    _switchTab(tabId) {
      const { navTabsInner, tabContentContainer } = this.elements;
      if (!tabContentContainer || !navTabsInner) return;

      const tabContentButtons = navTabsInner.querySelectorAll(this.classes.tabContentButton);

      // Remove active state from all buttons
      tabContentButtons.forEach((btn) => {
        btn.classList.remove(this.classes.active);
      });

      // Hide all content panels
      tabContentContainer.querySelectorAll('[data-tab-id]').forEach((content) => {
        content.classList.add(this.classes.hidden);
      });

      // Activate clicked buttons and slide nav to it
      const buttonsArray = Array.from(tabContentButtons);
      const activeButtons = buttonsArray.filter((btn) => btn.getAttribute('data-tab-id') === tabId);
      activeButtons.forEach((btn) => btn.classList.add(this.classes.active));

      if (this.navSwiperInstance && activeButtons.length > 0) {
        this.navSwiperInstance.slideTo(buttonsArray.indexOf(activeButtons[0]));
      }

      // Show corresponding content (match by attribute to avoid selector injection)
      /** @type {HTMLElement | null} */
      const activeContent =
        Array.from(tabContentContainer.querySelectorAll('[data-tab-id]')).find(
          (el) => el.getAttribute('data-tab-id') === tabId
        ) ?? null;
      if (activeContent) {
        activeContent.classList.remove(this.classes.hidden);
      }

      this._queueAlignItemsHeightRefresh(activeContent);
    }

    /**
     * Re-measures `<wlsn-align-items-height>` under the visible panel after layout.
     *
     * @param {HTMLElement | null} panel - `[data-tab-id]` node that is now visible.
     * @returns {void}
     */
    _queueAlignItemsHeightRefresh(panel) {
      if (!panel || !(panel instanceof HTMLElement)) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            panel.querySelectorAll('wlsn-align-items-height')?.forEach((host) => {
              if (typeof host.refresh === 'function') {
                host.refresh();
              }
            });
          });
        });
      });
    }
  }

  customElements.define('wlsn-tabs-switcher', WLSNTabsSwitcher);
}
